/** Shape-scoped formula name binding, independent of row classes and cell values. */
export class FormulaNameIndex<T> {
  readonly #labels = new Map<string, readonly T[]>();
  readonly #keys = new Map<string, readonly T[]>();

  /** Replace the complete name snapshot once after a structural or metadata change. */
  replace(inputs: readonly {
    readonly row: T;
    readonly label: string | null;
    readonly lookupKey: string | null;
    readonly identity: string;
    readonly source: boolean;
    readonly locator: string;
  }[]): void {
    this.#labels.clear();
    this.#keys.clear();
    for (const [field, index] of [["label", this.#labels], ["lookupKey", this.#keys]] as const) {
      const groups = new Map<string, typeof inputs[number][]>();
      for (const input of inputs) {
        const name = input[field];
        if (name == null) continue;
        const group = groups.get(name) ?? [];
        group.push(input);
        groups.set(name, group);
      }
      for (const [name, group] of groups) {
        const sources = group.filter(input => input.source);
        const unique = new Map<string, typeof inputs[number]>();
        for (const input of sources.length > 0 ? sources : group) {
          const prior = unique.get(input.identity);
          if (prior == null || input.locator < prior.locator) unique.set(input.identity, input);
        }
        index.set(name, Object.freeze([...unique.values()].sort((a, b) => a.locator.localeCompare(b.locator)).map(input => input.row)));
      }
    }
  }

  /** Lookup-key candidates take precedence over label candidates; no insertion-order winner. */
  candidates(name: string): readonly T[] {
    return this.#keys.get(name) ?? this.#labels.get(name) ?? [];
  }
}
